import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import ShippingIcon from '../../../assets/icons/ShipIcon';
import ReturnIcon from '../../../assets/icons/ReturnIcon';

import { ShippedKitsTable, ReturnedKitsTable } from '../../../components/kits';
import ActionButton from '../../../components/shared/ActionButton';
import Input from '../../../components/shared/Input';
import LoadScreen from '../../../components/load-screen';
import KitsFiltersModal from '../../../components/kits/KitsFiltersModal';
import KitFiltersList from '../../../components/kits/KitFiltersList';

import {
  toggleShipped,
  setFilters,
  toggleReturned,
  toggleConsignment,
  toggleLoan,
  toggleCanceled,
  toggleClosed,
} from '../../../actions/casesActions';
import { getSurgeons } from '../../../actions/surgeonsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getProcedures } from '../../../actions/proceduresActions';
import { getKits } from '../../../actions/kitsActions';
import { setOrder, setOrderBy, setPage, setRowsPerPage, setSearch, setActiveTab, getCases } from '../../../actions/shippingActions';

import { useDebounce, useLoading } from '../../../hooks';

import { caseStatusOptions, kitVariantTypes, setAllocationStatuses } from '../../../constants/enums';
import routes from '../../../constants/routes';

import './kits-page.scss';

// const { ASSIGNED, SHIPPED, RETURNED, TRANSFERRED, AVAILABLE, USAGE } = setAllocationStatuses;

const KitsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const surgeons = useSelector((state) => state.surgeons.list);
  const hospitals = useSelector((state) => state.hospitals.list);
  const procedures = useSelector((state) => state.procedures.list);
  const hideShipped = useSelector((state) => state.cases.hideShipped);
  const hideReturned = useSelector((state) => state.cases.hideReturned);
  const hideConsignment = useSelector((state) => state.cases.hideConsignment);
  const showCanceled = useSelector((state) => state.cases.showCanceled);
  const hideLoan = useSelector((state) => state.cases.hideLoan);
  const hideClosed = useSelector((state) => state.cases.hideClosed);
  const filters = useSelector((state) => state.cases.filters);

  const order = useSelector((state) => state.shipping.order);
  const orderBy = useSelector((state) => state.shipping.orderBy);
  const page = useSelector((state) => state.shipping.page);
  const rowsPerPage = useSelector((state) => state.shipping.rowsPerPage);
  const search = useSelector((state) => state.shipping.search);
  const activeTab = useSelector((state) => state.shipping.activeTab);

  const [isOpen, toggleModal] = useState(false);

  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const { loading, stopLoading } = useLoading();

  const debouncedSearch = useDebounce(search, 600);
  const debouncedFilters = useDebounce(filters, 200);

  useEffect(() => {
    onLoad().catch(console.error);
  }, []);

  const onLoad = async () => {
    try {
      await dispatch(getKits());
      await dispatch(getSurgeons());
      await dispatch(getHospitals());
      await dispatch(getProcedures());
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    dispatch(setPage(0));
  }, [hideShipped, hideReturned, hideConsignment, hideClosed, showCanceled, hideLoan, filters]);

  useEffect(() => {
    fetchCases().catch();
  }, [page, rowsPerPage, order, orderBy, debouncedSearch, debouncedFilters, hideLoan, hideConsignment, activeTab]);

  const fetchCases = async () => {
    try {
      setCasesLoading(true);

      let kitVariants = [kitVariantTypes.loan, kitVariantTypes.consignment];

      if (hideLoan) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.loan);
      }

      if (hideConsignment) {
        kitVariants = kitVariants.filter((key) => key !== kitVariantTypes.consignment);
      }

      if (hideLoan && hideConsignment) {
        setCases([]);
        setTotalRows(0);
        return;
      }

      const params = {
        search: debouncedSearch || '*',
        sortBy: orderBy || 'date',
        sortOrder: order || 'asc',
        page: page + 1, // page starts from 1
        perPage: rowsPerPage,
        active: true,
        kitVariants,
        hospitals: debouncedFilters.hospitals,
        states: debouncedFilters.states,
        surgeons: debouncedFilters.surgeons,
        procedures: debouncedFilters.procedures,
        // orderClosed: false,
      };

      if (showCanceled) {
        delete params.active;
      }

      // if (hideClosed) {
      //   params.orderClosed = false;
      // }

      if (activeTab === 0) {
        params.hasSetsToShip = true;
        params.statuses = [caseStatusOptions.request, caseStatusOptions.booked, caseStatusOptions.overdue];
      }
      if (activeTab === 1) {
        params.hasSetsToReturn = true;
      }

      const res = await dispatch(getCases(params));
      if (res && res.cases) {
        setCases(res.cases);
        setTotalRows(res.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCasesLoading(false);
    }
  };

  const onRowClick = (row) => {
    history.push(`${routes.CASES}/${row?.caseId}?from=shipping&view=SETS`);
  };

  const onRemoveFilter = (filter, value) => {
    dispatch(setPage(0));

    if (filter === 'consignment') {
      dispatch(toggleConsignment());
    }
    if (filter === 'loan') {
      dispatch(toggleLoan());
    }
    if (filter === 'shipped') {
      dispatch(toggleShipped());
    }
    if (filter === 'surgeon') {
      dispatch(setFilters({ surgeons: [...filters?.surgeons].filter((surgeon) => surgeon !== value) }));
    }
    if (filter === 'hospital') {
      dispatch(setFilters({ hospitals: [...filters?.hospitals].filter((hospital) => hospital !== value) }));
    }
    if (filter === 'state') {
      dispatch(setFilters({ states: [...filters?.states].filter((state) => state !== value) }));
    }
    if (filter === 'canceled') {
      dispatch(toggleCanceled(false));
    }
  };

  // const getSurgeonName = useCallback((surgeonId) => {
  //   const surgeon = surgeons.find((surgeon) => surgeon.id === surgeonId);
  //   return surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : '';
  // }, [surgeons, filters]);
  //
  // const getHospitalName = useCallback((hospitalId) => {
  //   const hospital = hospitals.find((hospital) => hospital.id === hospitalId);
  //   return hospital ? hospital.name : '';
  // }, [hospitals, filters]);
  //
  // const getProcedureName = useCallback((procedureId) => {
  //   const caseProcedure = procedures?.find((procedure) => procedure.id === procedureId);
  //   return caseProcedure && caseProcedure.name ? caseProcedure.name : '';
  // }, [cases, procedures]);
  //
  // const getHospitalState = useCallback((hospitalId) => {
  //   const hospital = hospitals?.find((hospital) => hospital.id === hospitalId);
  //   return hospital ? hospital.state : '';
  // }, [hospitals]);

  // Memoized data

  // const list = useMemo(() => {
  //   let result = [...cases];
  //
  //   if (hideConsignment) {
  //     result = result.filter((item) => item.kitVariant !== kitVariantTypes.consignment);
  //   }
  //
  //   if (hideLoan) {
  //     result = result.filter((item) => item.kitVariant !== kitVariantTypes.loan);
  //   }
  //
  //   if (hideClosed) {
  //     result = result.filter((item) => !item.orderClosed);
  //   }
  //
  //   if (filters.surgeons?.length) {
  //     result = result.filter((item) => filters.surgeons?.includes(item.surgeon));
  //   }
  //
  //   if (filters.hospitals?.length) {
  //     result = result.filter((item) => filters.hospitals?.includes(item.hospital));
  //   }
  //
  //   if (filters.states?.length) {
  //     const stateHospitals = hospitals?.filter((h) => filters.states?.includes(h.state));
  //     result = result.filter((item) => stateHospitals?.map((h) => h.id)?.includes(item.hospital));
  //   }
  //
  //   return cases
  //     .map((item) => ({
  //       ...item,
  //       surgeonName: getSurgeonName(item.surgeon),
  //       hospitalName: getHospitalName(item.hospital),
  //       hospitalState: getHospitalState(item.hospital),
  //       procedureName: getProcedureName(item.procedure),
  //     }))
  //     .filter((item) => item?.patientReference?.toLowerCase()?.includes(search.toLowerCase()) || item?.id?.toLowerCase()?.includes(search.toLowerCase()));
  // }, [cases, hospitals, getHospitalState, getHospitalName, getSurgeonName, getProcedureName]);

  // const shippedCases = useMemo(() => {
  //   return list.filter((item) => {
  //     const date = moment().subtract(7, 'days').endOf('day');
  //     const caseDate = moment(item.date).endOf('day');
  //
  //     const isShipped = item.setsAllocation && !item.setsAllocation.some((allocation) => [ASSIGNED.value, AVAILABLE.value].includes(allocation.status));
  //     return !!item.active && !date.isAfter(caseDate) && !isShipped;
  //   });
  // }, [list]);
  //
  // const returnedCases = useMemo(() => {
  //   return list.filter((item) => {
  //     let filter;
  //
  //     // if (hideReturned) {
  //     //   const total = item.setsAllocation ? item.setsAllocation.filter((item) => [ASSIGNED.value, SHIPPED.value, RETURNED.value].includes(item.status)) : [];
  //     //   const isReturned = total.every((allocation) => allocation.status === RETURNED.value);
  //     //
  //     //   filter = !isReturned;
  //     // } else {
  //     //   filter = item.setsAllocation.some((allocation) => [ASSIGNED.value, SHIPPED.value, RETURNED.value, TRANSFERRED.value].includes(allocation.status));
  //     // }
  //
  //     const total = item.setsAllocation ? item.setsAllocation.filter((item) => [ASSIGNED.value, SHIPPED.value, RETURNED.value, USAGE.value].includes(item.status)) : [];
  //     const isReturned = total.every((allocation) => allocation.status === RETURNED.value || allocation.status === USAGE.value);
  //     const isShipped = total.every((allocation) => allocation.status === SHIPPED.value || allocation.status === RETURNED.value || allocation.status === USAGE.value);
  //     const hasShippedKits = item.setsAllocation && !!item.setsAllocation.filter((allocation) => [SHIPPED.value, RETURNED.value, USAGE.value, TRANSFERRED.value].includes(allocation.status)).length;
  //
  //     if (hideShipped) {
  //       const today = moment().startOf('day');
  //       const caseDate = moment(item.date).endOf('day');
  //
  //       filter = (!isShipped || today.isAfter(caseDate)) && item.setsAllocation.some((allocation) => [ASSIGNED.value, SHIPPED.value, RETURNED.value, USAGE.value, TRANSFERRED.value].includes(allocation.status));
  //     } else {
  //       filter = item.setsAllocation.some((allocation) => [ASSIGNED.value, SHIPPED.value, RETURNED.value, USAGE.value, TRANSFERRED.value].includes(allocation.status));
  //     }
  //
  //     // if (item.kitVariant === kitVariantTypes.consignment) {
  //     //   filter = item.status !== caseStatusOptions.completed;
  //     // }
  //
  //     return filter && hasShippedKits && !isReturned;
  //   });
  // }, [list, hideShipped]);

  const states = useMemo(() => {
    const list = [];
    hospitals?.forEach((hospital) => {
      if (!list?.includes(hospital.state)) {
        list.push(hospital.state);
      }
    });
    return [
      { key: 'all', label: 'All', value: '' },
      ...list?.map((state, index) => ({
        key: `${state}_${index}`,
        label: state,
        value: state,
      }))
    ];
  }, [hospitals]);

  return (
    <div className='page-container kits-page'>
      {loading && <LoadScreen />}

      <div className='d-flex space-between'>
        <div className='d-flex'>
          <div className='page-title'>
            Shipping
          </div>
        </div>
      </div>

      <div className='kits-page__body'>
        <div className='d-flex space-between m-b-md'>
          <div className='d-flex'>
            <div className='m-r-md'>
              <ActionButton
                onClick={() => {
                  dispatch(setActiveTab(0));
                  dispatch(setPage(0));
                }}
                bgColor={activeTab === 0 ? setAllocationStatuses.SHIPPED.color : '#dddddd'}
              >
                <ShippingIcon color="#ffffff" />
                Ship
              </ActionButton>
            </div>
            <div className='m-l-md'>
              <ActionButton
                onClick={() => {
                  dispatch(setActiveTab(1));
                  dispatch(setPage(0));
                }}
                bgColor={activeTab === 1 ? setAllocationStatuses.RETURNED.color : '#dddddd'}
              >
                <ReturnIcon color="#ffffff" />
                Return
              </ActionButton>
            </div>
          </div>

          <div className='d-flex flex-end' style={{ width: '40%' }}>
            <div className='flex-1' style={{ maxWidth: 500 }}>
              <Input
                type='search'
                placeholder='Search Cases (Patient ref or Case #)'
                value={search}
                onChange={(e) => {
                  dispatch(setPage(0));
                  dispatch(setSearch(e.target.value));
                }}
              />
            </div>
          </div>
        </div>

        <KitFiltersList
          onClick={() => toggleModal(true)}
          hideShipped={hideShipped}
          hideReturned={hideReturned}
          hideConsignment={hideConsignment}
          hideLoan={hideLoan}
          hideClosed={hideClosed}
          activeTab={activeTab}
          surgeons={filters?.surgeons}
          hospitals={filters?.hospitals}
          states={filters?.states}
          surgeonsList={surgeons}
          hospitalsList={hospitals}
          onDelete={onRemoveFilter}
          showCanceled={showCanceled}
          isShipping
        />

        {activeTab === 0 ? (
          <ShippedKitsTable
            cases={cases}
            onRowClick={onRowClick}
            surgeons={surgeons}
            hospitals={hospitals}
            procedures={procedures}
            order={order}
            setOrder={(value) => dispatch(setOrder(value))}
            orderBy={orderBy}
            setOrderBy={(value) => dispatch(setOrderBy(value))}
            page={page}
            setPage={(value) => dispatch(setPage(value))}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}
            loading={casesLoading}
            totalRows={totalRows}
          />
        ) : (
          <ReturnedKitsTable
            cases={cases}
            onRowClick={onRowClick}
            surgeons={surgeons}
            hospitals={hospitals}
            procedures={procedures}
            order={order}
            setOrder={(value) => dispatch(setOrder(value))}
            orderBy={orderBy}
            setOrderBy={(value) => dispatch(setOrderBy(value))}
            page={page}
            setPage={(value) => dispatch(setPage(value))}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(value) => dispatch(setRowsPerPage(value))}
            loading={casesLoading}
            totalRows={totalRows}
          />
        )}
      </div>

      <KitsFiltersModal
        open={isOpen}
        onClose={() => toggleModal(false)}
        activeTab={activeTab}
        hideShipped={hideShipped}
        hideReturned={hideReturned}
        hideConsignment={hideConsignment}
        hideLoan={hideLoan}
        hideClosed={hideClosed}
        showCanceled={showCanceled}
        toggleShipped={() => dispatch(toggleShipped())}
        toggleReturned={() => dispatch(toggleReturned())}
        toggleConsignment={() => dispatch(toggleConsignment())}
        toggleCanceled={(value) => dispatch(toggleCanceled(value))}
        toggleClosed={(value) => dispatch(toggleClosed(value))}
        toggleLoan={() => dispatch(toggleLoan())}
        selectedSurgeons={filters.surgeons}
        selectedHospitals={filters.hospitals}
        selectedStates={filters.states}
        onSurgeonChange={(values) => dispatch(setFilters({ surgeons: values }))}
        onHospitalChange={(values) => dispatch(setFilters({ hospitals: values }))}
        onStateChange={(values) => dispatch(setFilters({ states: values }))}
        surgeons={surgeons}
        hospitals={hospitals}
        states={states}
      />
    </div>
  );
};

export default KitsPage;
